import {
  FC,
  ComponentProps,
  Ref,
  forwardRef,
  useRef,
  useState,
  useEffect,
  useMemo,
} from 'react';

import moment from 'moment';
import { throttle } from 'lodash';

import { generateTimeRange } from 'utils/date';
import useScrollArea from '../../hooks/useScrollArea';

import Row from './row';

import OpeningHours from './openingHours';

const horizontal = true;

interface Venue {
  id: number;
}

interface BookingSlot {
  date: string;
  startTime: string;
  endTime: string;
}

interface Props {
  name?: string;
  venue: Venue;
  spaces: any;
  openingTime?: string;
  closingTime?: string;
  clinical?: boolean;
  slots?: BookingSlot[];
  bookingItems?: ComponentProps<typeof Row>['bookingItems'];
  onChangeSpace?: (date: string, space: any) => void;
  removeDate?: (date: string) => void;
  removeSpace?: (date: string, spaceId: number) => void;
  touched?: object;
  setTouched?: (touched: object) => void;
  value: object;
  setValue?: (value: object) => void;
  availability: object;
  disabled?: boolean;
  stickyHeaderEnabled?: boolean;
  spaceDetailsComponent?: ComponentProps<typeof Row>['spaceDetailsComponent'];
  headerComponent?: ComponentProps<typeof Row>['headerComponent'];
  loading?: boolean;
  bookingState: string;
}

const HEADER_OFFSET = 65;

const emptyObject = {};
const emptyList = [];
const noOp = () => null;

const wrapSetter = (
  setter: (value: object) => void,
  value: object = {},
  key: string
) => (innerValue: any) => setter({ ...value, [key]: innerValue });

const Calendar: FC<Props> = (
  {
    name = 'bookingItems',
    venue,
    spaces,
    openingTime,
    closingTime,
    clinical,
    slots = emptyList,
    bookingItems = emptyList,
    touched = emptyObject,
    setTouched = noOp,
    value = emptyObject,
    setValue = noOp,
    availability = emptyObject,
    disabled,
    onChangeSpace,
    removeDate,
    removeSpace,
    stickyHeaderEnabled = true,
    spaceDetailsComponent,
    headerComponent,
    loading,
    bookingState = 'tentative',
  },
  forwardedRef: Ref
) => {
  const {
    scrollRef,
    scrollState,
    setScrollState,
    scrollToPosition,
  } = useScrollArea(horizontal);

  const openingTimes = useMemo(
    () => generateTimeRange(openingTime, closingTime, false),
    [openingTime, closingTime]
  );

  const rows = useMemo(
    () =>
      Object.keys(value)
        .sort((a, b) => new Date(a) - new Date(b))
        .map(date => ({
          name: `${name}.${date}`,
          date,
          bookingItems: bookingItems.filter(({ startTime }) =>
            moment(startTime).isSame(date, 'day')
          ),
          slots: slots.filter(slot => slot.date === date),
          spaces: Object.keys(value[date])
            .map(spaceId => spaces.find(({ id }) => `s${id}` === spaceId))
            .filter(space => !!space),
          touched: touched[date],
          setTouched: wrapSetter(setTouched, touched, date),
          value: value[date],
          setValue: wrapSetter(setValue, value, date),
          unconfirmed: bookingState !== 'confirmed',
        })),
    [
      name,
      bookingState,
      bookingItems,
      slots,
      spaces,
      touched,
      setTouched,
      value,
      setValue,
    ]
  );

  const setRef = date => ref => {
    // eslint-disable-next-line no-param-reassign
    if (forwardedRef) forwardedRef.current[date] = ref;
  };

  const [stickyHeaderOpen, setStickyHeaderOpen] = useState<boolean>(false);

  const stickyHeaderRef = useRef(null);

  useEffect(() => {
    const onScroll = throttle(() => {
      if (stickyHeaderRef.current) {
        const { offsetTop } = stickyHeaderRef.current;
        const { pageYOffset } = window;
        setStickyHeaderOpen(pageYOffset > offsetTop - HEADER_OFFSET);
      }
    }, 500);

    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const renderRow = ({
    name,
    date,
    bookingItems,
    slots,
    spaces,
    touched,
    setTouched,
    value,
    setValue,
    unconfirmed,
  }) => (
    <div key={date}>
      <Row
        ref={setRef(date)}
        name={name}
        date={date}
        times={openingTimes}
        venue={venue}
        spaces={spaces}
        clinical={clinical}
        startTime={openingTime}
        endTime={closingTime}
        slots={slots}
        bookingItems={bookingItems}
        scrollRef={scrollRef}
        scrollState={scrollState}
        availability={availability[date]}
        disabled={disabled}
        onChangeSpace={onChangeSpace}
        onRemove={
          unconfirmed && removeDate ? () => removeDate(date) : undefined
        }
        onRemoveSpace={unconfirmed ? removeSpace : undefined}
        spaceDetailsComponent={spaceDetailsComponent}
        headerComponent={headerComponent}
        loading={loading}
        confirmed={!unconfirmed}
        touched={touched}
        setTouched={setTouched}
        value={value}
        setValue={setValue}
      />
    </div>
  );

  return (
    <div className="calendar" ref={stickyHeaderRef}>
      <OpeningHours
        openingTimes={openingTimes}
        scrollRef={scrollRef}
        scrollState={scrollState}
        setScrollState={setScrollState}
        scrollToPosition={scrollToPosition}
        sticky={stickyHeaderEnabled && stickyHeaderOpen}
      />
      {rows.map(renderRow)}
    </div>
  );
};

export default forwardRef(Calendar);
